import { PageHeader } from "@/components/PageHeader";
import { SENDER_IDENTITY_LABELS } from "@/components/SenderIdentitySelect";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { EmailPreview } from "./components/EmailPreview";
import type { ProductEmailStatus } from "./types";

const STATUS_COLORS: Record<ProductEmailStatus, string> = {
  draft: "zinc",
  sending: "blue",
  sent: "green",
  partial_failure: "amber",
  failed: "red",
};

export default function SendDetailPage() {
  const { sendId } = useParams<{ sendId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["product-email-send", sendId],
    queryFn: () => api.getSendDetail(sendId!),
    enabled: !!sendId,
  });

  const send = data?.send;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
      </div>
    );
  }

  if (!send) {
    return (
      <div className="px-4 py-0">
        <PageHeader title="Send Not Found" />
        <p className="text-zinc-500">
          The requested email send could not be found.
        </p>
        <Button
          plain
          className="mt-4"
          onClick={() => navigate("/admin/product-emails")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Product Emails
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-0">
      <div className="mb-4">
        <Button
          plain
          onClick={() => navigate("/admin/product-emails")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Product Emails
        </Button>
      </div>

      <PageHeader title={send.subject}>
        <Badge
          color={
            STATUS_COLORS[send.status] as
              | "zinc"
              | "blue"
              | "green"
              | "amber"
              | "red"
          }
        >
          {send.status.replace("_", " ")}
        </Badge>
      </PageHeader>

      <div className="flex gap-8">
        {/* Left: Metadata */}
        <div className="w-80 shrink-0 space-y-6">
          <div className="space-y-4 rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              Details
            </h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">Sender</dt>
                <dd className="mt-0.5 font-medium text-zinc-900 dark:text-white">
                  {SENDER_IDENTITY_LABELS[send.senderIdentity] ?? send.senderIdentity}
                </dd>
              </div>
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">
                  Recipients
                </dt>
                <dd className="mt-0.5 font-medium text-zinc-900 dark:text-white">
                  {send.recipientCount}{" "}
                  {send.recipientCount === 1 ? "user" : "users"}
                  {send.isTest && (
                    <Badge color="purple" className="ml-2">
                      Test
                    </Badge>
                  )}
                </dd>
              </div>
              {send.testRecipient && (
                <div>
                  <dt className="text-zinc-500 dark:text-zinc-400">
                    Test Recipient
                  </dt>
                  <dd className="mt-0.5 font-medium text-zinc-900 dark:text-white">
                    {send.testRecipient}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">
                  Input Mode
                </dt>
                <dd className="mt-0.5 font-medium text-zinc-900 dark:text-white">
                  {send.inputMode}
                </dd>
              </div>
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">
                  Created At
                </dt>
                <dd className="mt-0.5 text-zinc-900 dark:text-white">
                  {formatDate(send.createdAt)}
                </dd>
              </div>
              {send.sentAt && (
                <div>
                  <dt className="text-zinc-500 dark:text-zinc-400">Sent At</dt>
                  <dd className="mt-0.5 text-zinc-900 dark:text-white">
                    {formatDate(send.sentAt)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">Sent By</dt>
                <dd className="mt-0.5 text-zinc-900 dark:text-white">
                  {send.sentBy}
                </dd>
              </div>
              {send.errorSummary && (
                <div>
                  <dt className="text-zinc-500 dark:text-zinc-400">Error</dt>
                  <dd className="mt-0.5 text-red-600 dark:text-red-400">
                    {send.errorSummary}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Per-recipient results */}
          {send.sendResults && send.sendResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                Recipient Results ({send.sendResults.length})
              </h3>
              <Table dense>
                <TableHead>
                  <TableRow>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {send.sendResults.map((result) => (
                    <TableRow key={result.email}>
                      <TableCell className="text-xs">
                        {result.email}
                      </TableCell>
                      <TableCell>
                        {result.success ? (
                          <Badge color="green">Sent</Badge>
                        ) : (
                          <Badge color="red" title={result.error}>
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Right: Email Preview */}
        <div className="flex-1">
          <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">
            Email Preview
          </h3>
          <EmailPreview
            html={send.renderedHtml ?? ""}
            viewMode="desktop"
          />
        </div>
      </div>
    </div>
  );
}
